import type { JourneyFilter } from '../data/DataSource'
import type { Aggregator, AggStatus, Ticket } from '../domain/types'
import { scheduleDate, scheduleInstant } from '../domain/scheduling'

export interface JourneyListFilters {
  statuses: AggStatus[]
  services: string[]
  attendants: string[]
  sessions: string[]
  from: string
  to: string
  hideAuto: boolean
}

export const EMPTY_JOURNEY_FILTERS: JourneyListFilters = {
  statuses: [], services: [], attendants: [], sessions: [], from: '', to: '', hideAuto: false,
}

export interface JourneyListViewState {
  group: string
  filters: JourneyListFilters
}

export const EMPTY_JOURNEY_LIST_STATE: JourneyListViewState = { group: 'ativos', filters: EMPTY_JOURNEY_FILTERS }

export function matchesJourneyTicketFilters(journey: Aggregator, filters: JourneyListFilters): boolean {
  const { services, attendants, sessions, from, to } = filters
  if (!services.length && !attendants.length && !sessions.length && !from && !to) {return true}
  return journey.tickets.some((ticket: Ticket) => {
    if (services.length && !services.includes(String(ticket.serviceId))) {return false}
    if (attendants.length && !attendants.includes(String(ticket.resourceId ?? ''))) {return false}
    if (sessions.length && !sessions.includes(String(ticket.sessionId ?? ''))) {return false}
    if (!from && !to) {return true}
    const value = ticket.sessionStart ?? journey.metadata.fields.$journeyBooking
    if (!value) {return false}
    const instant = scheduleInstant(value.length === 10 ? `${value}T12:00:00` : value)
    if (!Number.isFinite(instant)) {return false}
    const day = scheduleDate(new Date(instant))
    return (!from || day >= from) && (!to || day <= to)
  })
}

export function journeyDays(journeys: Aggregator[]): { date: string; hasCompleteJourney: boolean; journeyCount: number }[] {
  const counts = new Map<string, number>()
  for (const journey of journeys) {
    if (journey.status === 'Canceled' || journey.tickets.every((ticket) => ticket.status === 'Canceled')) {continue}
    const dates = new Set<string>()
    for (const ticket of journey.tickets) {
      if (ticket.status === 'Canceled' || !ticket.sessionStart) {continue}
      const instant = scheduleInstant(ticket.sessionStart)
      if (Number.isFinite(instant)) {dates.add(scheduleDate(new Date(instant)))}
    }
    const booked = journey.metadata.fields.$journeyBooking
    if (!dates.size && booked && /^\d{4}-\d{2}-\d{2}$/.test(booked)) {dates.add(booked)}
    for (const date of dates) {counts.set(date, (counts.get(date) ?? 0) + 1)}
  }
  return [...counts].map(([date, journeyCount]) => ({ date, hasCompleteJourney: true, journeyCount }))
}

export function journeyListRequest(filters: JourneyListFilters, group: string, search: string, dateFilter: 'today' | 'all'): JourneyFilter {
  return {
    dateFilter: filters.from || filters.to ? 'all' : dateFilter,
    group: group === 'ativos' ? 'active' : group === 'cancelados' ? 'canceled' : 'all',
    q: search.trim() || undefined,
    sessionFrom: filters.from || undefined,
    sessionTo: filters.to || undefined,
    serviceIds: filters.services.length ? filters.services.map(Number) : undefined,
    resourceIds: filters.attendants.length ? filters.attendants.map(Number) : undefined,
    sessionIds: filters.sessions.length ? filters.sessions.map(Number) : undefined,
    situations: filters.statuses.length ? filters.statuses : undefined,
    hideAuto: filters.hideAuto || undefined,
    pageSize: 15,
  }
}
